/**
 * Radar Scouta (sekcja na Pulpicie) — podpowiedzi z rynku prowadzące do akcji.
 * 5 kart: okazja z branży, pytanie klientów, pomysł na post, miejsce promocji, sygnał konkurencji.
 * Karty z cache (odświeżanie na żądanie).
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import {
  RadarIcon, TrendingUp, MessageCircleQuestion, PenLine, MapPin, Target,
  Loader2, RefreshCw, ListPlus, Check, ArrowRight,
} from './scoutRadarIcons';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';
const chip = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide';

const CARD_META = {
  okazja: { label: 'Okazja z branży', color: '#F59E0B', icon: TrendingUp },
  pytanie: { label: 'Pytanie klientów', color: '#3B82F6', icon: MessageCircleQuestion },
  post: { label: 'Pomysł na post', color: '#8B5CF6', icon: PenLine },
  miejsce: { label: 'Miejsce promocji', color: '#10B981', icon: MapPin },
  konkurencja: { label: 'Sygnał konkurencji', color: '#EC4899', icon: Target },
};

const ACTION_OPENINGS = {
  post: (s) => `Przygotuj post na temat: ${s}`,
  odpowiedz: (s) => `Klienci często pytają: ${s} Przygotuj odpowiedź, którą mogę wykorzystać.`,
  oferta: (s) => `Potrzebuję oferty. Kontekst z radaru: ${s}`,
  konkurencja: (s) => `Chcę sprawdzić konkurencję i znaleźć swoje przewagi. Kontekst: ${s}`,
};

export default function ScoutRadarPulpit({ client, startDialog }) {
  const [cards, setCards] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState('');
  const [planned, setPlanned] = useState({});

  useEffect(() => {
    let live = true;
    setCards(null); setPlanned({}); setErr('');
    fetch(`${API}/scout/radar-cards/${client.id}`)
      .then((r) => r.json())
      .then((d) => { if (live) { setCards(d?.ok ? d.cards : []); setGeneratedAt(d?.generated_at || null); } })
      .catch(() => { if (live) setCards([]); });
    return () => { live = false; };
  }, [client.id]);

  const refresh = async () => {
    if (generating) return;
    setGenerating(true); setErr('');
    try {
      const res = await fetch(`${API}/scout/radar-cards/refresh`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id }),
      });
      const d = await res.json();
      if (d?.ok) { setCards(d.cards); setGeneratedAt(d.generated_at); setPlanned({}); }
      else setErr(d?.message || 'Nie udało się przygotować radaru.');
    } catch { setErr('Błąd połączenia. Spróbuj ponownie.'); }
    finally { setGenerating(false); }
  };

  const addToPlan = async (card, idx) => {
    if (planned[idx]) return;
    try {
      const res = await fetch(`${API}/scout/plan/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id, items: [card.seed || card.text], source: 'Radar Scouta' }),
      });
      const d = await res.json();
      if (d?.ok) setPlanned((s) => ({ ...s, [idx]: true }));
    } catch { /* noop */ }
  };

  const act = (card, idx) => {
    if (card.action === 'plan') { addToPlan(card, idx); return; }
    const opening = ACTION_OPENINGS[card.action]?.(card.seed || card.text);
    if (opening) startDialog(opening);
  };

  return (
    <section data-testid="radar-scouta">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><RadarIcon className="w-5 h-5 text-[#FF8A3D]" /> Radar Scouta</h2>
          <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Pomysły, okazje i sygnały z rynku — Scout podpowiada, co warto zrobić.</p>
        </div>
        {cards?.length > 0 && (
          <button onClick={refresh} disabled={generating} data-testid="radar-refresh-btn" title="Odśwież podpowiedzi"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--sc-border2)] px-3 py-2 text-xs font-semibold text-[var(--sc-t50)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)] transition shrink-0 disabled:opacity-50">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Odśwież
          </button>
        )}
      </div>

      {cards === null && <div className={`${panel} p-6 text-center`}><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FF8A3D]" /></div>}

      {cards !== null && cards.length === 0 && (
        <div className={`${panel} p-6 text-center`} data-testid="radar-empty">
          <RadarIcon className="w-8 h-8 mx-auto text-[var(--sc-t15)] mb-2" />
          <p className="text-sm text-[var(--sc-t50)] mb-3">Scout przygotuje 5 podpowiedzi dopasowanych do Twojej branży i sezonu.</p>
          <button onClick={refresh} disabled={generating} data-testid="radar-generate-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-4 py-2.5 text-sm font-bold text-white transition disabled:opacity-50">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RadarIcon className="w-4 h-4" />}
            {generating ? 'Scout nasłuchuje rynku…' : 'Pokaż podpowiedzi'}
          </button>
        </div>
      )}
      {err && <p className="text-xs text-red-400 mt-2" data-testid="radar-error">{err}</p>}

      {cards?.length > 0 && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="radar-cards">
          {cards.map((c, i) => {
            const meta = CARD_META[c.type] || CARD_META.okazja;
            const Icon = meta.icon;
            return (
              <div key={i} className={`${panel} p-4 flex flex-col gap-2.5`} data-testid={`radar-card-${c.type}`}>
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1e` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  </span>
                  <span className={chip} style={{ background: `${meta.color}18`, color: meta.color }}>{meta.label}</span>
                </div>
                <div className="text-sm font-bold leading-snug">{c.title}</div>
                <p className="text-xs text-[var(--sc-t55)] leading-relaxed flex-1">{c.text}</p>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => act(c, i)} data-testid={`radar-action-${c.type}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500/12 border border-orange-400/30 px-3 py-1.5 text-xs font-bold text-orange-300 hover:bg-orange-500/20 transition">
                    {c.action === 'plan' && planned[i] ? <Check className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
                    {c.action === 'plan' && planned[i] ? 'W planie' : (c.action_label || 'Działaj')}
                  </button>
                  {c.action !== 'plan' && (
                    <button onClick={() => addToPlan(c, i)} data-testid={`radar-plan-${c.type}`} title="Dodaj do planu działań"
                      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${planned[i] ? 'text-emerald-400' : 'text-[var(--sc-t40)] hover:text-[var(--sc-t80)] hover:bg-[var(--sc-hover)]'}`}>
                      {planned[i] ? <Check className="w-3.5 h-3.5" /> : <ListPlus className="w-3.5 h-3.5" />}
                      {planned[i] ? 'W planie' : 'Do planu'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {generatedAt && cards?.length > 0 && (
        <p className="text-[10px] text-[var(--sc-t30)] mt-2 text-right">Podpowiedzi z {generatedAt.slice(0, 10)} · odświeżaj, gdy chcesz świeże sygnały</p>
      )}
    </section>
  );
}
